import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Observable } from 'rxjs';
import {
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsNumberValueProperty,
  ServiceTimeBasedCommands,
  ServiceTimeBasedProperties,
  VentilationFilterRuntime,
  VentilationHeatExchangerFrostprotectionProperties,
  VentilationLevelsProperties,
  VentilationPreHeaterStatisticsProperties,
} from '@vi-iot/client';

import { InstallationFeaturesStore } from '@store';
import { concatLatestFrom } from '@ngrx/effects';
import { map } from 'rxjs/operators';
import moment from 'moment';
import { VentilationDiagnosticsHelpers } from './ventilation-diagnostic-helpers';

@Component({
  selector: 'app-ventilation-diagnostic-information',
  templateUrl: './ventilation-diagnostic-information.component.html',
  styleUrls: ['./ventilation-diagnostic-information.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VentilationDiagnosticInformationComponent implements OnInit {
  readonly featurePropertyKey = FeaturePropertyKey;
  readonly Number = Number;
  readonly VentilationDiagnosticsHelpers = VentilationDiagnosticsHelpers;

  heatExchangerfrostProtection$: Observable<
    Feature<VentilationHeatExchangerFrostprotectionProperties, never>
  >;
  preHeaterStatistics$: Observable<Feature<VentilationPreHeaterStatisticsProperties, never>>;
  filterRuntime$: Observable<Feature<VentilationFilterRuntime, never>>;
  deviceTimeOffset$: Observable<Feature<IsNumberValueProperty, never>>;
  heatingServiceTimeBased$: Observable<
    Feature<ServiceTimeBasedProperties, ServiceTimeBasedCommands>
  >;
  lastFilterChange$: Observable<Date>;
  nextFilterChange$: Observable<Date>;
  ventilationLevelOne$: Observable<Feature<VentilationLevelsProperties, never>>;
  ventilationLevelTwo$: Observable<Feature<VentilationLevelsProperties, never>>;
  ventilationLevelThree$: Observable<Feature<VentilationLevelsProperties, never>>;
  ventilationLevelFour$: Observable<Feature<VentilationLevelsProperties, never>>;

  constructor(private installationFeaturesStore: InstallationFeaturesStore) {}

  ngOnInit(): void {
    this.heatExchangerfrostProtection$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationHeatExchangerFrostprotection
    );

    this.preHeaterStatistics$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.preHeaterStatistics
    );

    this.filterRuntime$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.filterRuntime
    );

    this.deviceTimeOffset$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDeviceTimeOffset
    );

    this.heatingServiceTimeBased$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.serviceTimeBased
    );

    this.ventilationLevelOne$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationProgramLevels.levelOne
    );

    this.ventilationLevelTwo$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationProgramLevels.levelTwo
    );

    this.ventilationLevelThree$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationProgramLevels.levelThree
    );

    this.ventilationLevelFour$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.ventilationProgramLevels.levelFour
    );

    const localDate$ = this.deviceTimeOffset$.pipe(
      map((feature) =>
        moment()
          .utcOffset(0)
          .add(feature?.properties?.value?.value, this.featurePropertyKey.minutes)
      )
    );

    this.lastFilterChange$ = this.filterRuntime$.pipe(
      concatLatestFrom(() => localDate$),
      map(([filterRuntime, localDate]) =>
        localDate
          .subtract(filterRuntime?.properties?.operatingHours?.value, this.featurePropertyKey.hours)
          .toDate()
      )
    );

    this.nextFilterChange$ = this.filterRuntime$.pipe(
      concatLatestFrom(() => localDate$),
      map(([filterRuntime, localDate]) =>
        localDate
          .add(filterRuntime?.properties?.remainingHours?.value, this.featurePropertyKey.hours)
          .toDate()
      )
    );
  }
}
